import React, { useState } from "react";
import { DISMISS, useNotification } from "collections/Notifications";
import {
  FeatureFlagEnabled,
  LoadingIndicator,
  NoResultsIndicator,
  ProductImageInContainer,
  ToolTip
} from "elements";
import { noOpNotifyContext } from "polygraphql";
import { useTranslation } from "utilities/i18n";
import {
  NavLink,
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch
} from "polyrouter";
import { Layout } from "views/Main/Layouts";
import {
  useDeviceDetailsQuery,
  useDeviceSettingsAvailabilityQuery,
  useProvisioningAccountsQuery
} from "generated/graphql";
import { constructFilterList } from "utilities/filters/deviceFilters";
import { compareVersion, formatVersion } from "utilities/stringUtils";
import { DetailProps, DeviceDetails } from "./DeviceDetails";
import EditDeviceDetailsModal from "routes/Manage/Inventory/Edit/EditDeviceDetailsModal";
import DeleteDeviceModal from "routes/Manage/Inventory/Delete/DeleteDeviceModal";
import DeviceSettingsRouter, {
  MIN_SOFTWARE_FOR_ADVANCED
} from "routes/Manage/Inventory/Settings/DeviceSettingsRouter";
import {
  ProvisionedDeviceSettingsRouter,
  ProvisionedSettingsNav
} from "routes/Manage/Inventory/Settings/ProvisionedDeviceSettingsRouter";
import { ProtectedRoute } from "TsRoleGuards";
import {
  deviceHasExtraOptions,
  isEdge,
  isEdgeE,
  isLensRelay,
  isPolyOS
} from "utilities/modelSelectors";
import { generatePath } from "react-router";
import { DeviceHeader } from "../DeviceHeader";
import { RestartDeviceModal } from "./RestartDeviceModal";
import { FactoryResetDeviceModal } from "./FactoryResetDeviceModal";
import {
  RelaySettingsLoader,
  RelaySettingsNav
} from "../Settings/RelayDeviceSettingsRouter";
import { DiagnosticsNav } from "./Diagnostics/DiagnosticsNav";
import { DiagnosticsRouter } from "./Diagnostics/DiagnosticsRouter";
import DesktopDeviceSettingsRouter from "../Settings-Desktop/DesktopDeviceSettingsRouter";
import {
  DEVICE_SETTINGS,
  SettingsNav,
  SettingsNavDesktop
} from "../Settings-Shared/DeviceSettingsComponents";
import {
  DESKTOP_PROXY_AGENT,
  DIAGNOSTICS_SUPPORT,
  MIN_SOFTWARE_FOR_EDGE_LOG_DOWNLOAD,
  MIN_SOFTWARE_FOR_POLYOS_LOG_DOWNLOAD,
  MIN_SOFTWARE_FOR_POLYOS_LOG_FLASH
} from "Constants";
import { DeviceAnalysis } from "./DeviceAnalysis";
import {
  useIsLensApp,
  useTenantRelayDevices,
  useIsConnectedToApp,
  useIsCompatibleVersion
} from "./hooks";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import { DialOutModal } from "../DialOut/DialOutModal";

export function useDeviceDetails(deviceId: string) {
  return useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: constructFilterList({ id: deviceId })
      }
    },
    pause: !deviceId
  });
}

export function useDeviceDetailsReadOnly(deviceId: string) {
  const { data } = useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: constructFilterList({ id: deviceId })
      }
    },
    requestPolicy: "cache-only",
    pause: !deviceId
  });
  return data?.devices[0];
}

export const useDeviceSettingsRouteMatch = () =>
  useRouteMatch<{ category: string; deviceId: string }>();

export const DeviceLoader: React.FC = () => {
  const tenantId = useTenantId();
  const { deviceId } = useParams<{ deviceId: string }>();
  const { data: deviceData, fetching: deviceFetching } = useDeviceDetails(
    deviceId
  );

  const {
    data: provData,
    fetching: provFetching
  } = useProvisioningAccountsQuery({
    variables: { tenantUuid: tenantId }
  });

  const {
    data: devSettingsData,
    fetching: devSettingsFetching,
    error: devSettingsError
  } = useDeviceSettingsAvailabilityQuery({
    variables: { id: deviceId },
    context: noOpNotifyContext
  });

  const fetching = deviceFetching || provFetching || devSettingsFetching;
  const device = deviceData?.devices.length ? deviceData.devices[0] : undefined;
  if (!fetching && device && provData) {
    const account = provData.getProvisioningAccounts?.[0];
    const provDisabled = !account || !!account.isDisabled;

    let deviceSettingsNotFound: boolean;
    let deviceProvEnabled: boolean;

    if (device?.hardwareModel && isPolyOS(device.hardwareModel)) {
      deviceSettingsNotFound =
        Boolean(devSettingsError) ||
        !Boolean(devSettingsData?.deviceSettings?.settings);

      deviceProvEnabled = Boolean(
        devSettingsData?.deviceSettings?.settings?.prov?.enabled
      );
    } else {
      deviceProvEnabled = true;
      deviceSettingsNotFound = false;
    }

    return (
      <DeviceRouter
        device={device}
        id={deviceId}
        deviceProvEnabled={deviceProvEnabled}
        accountProvEnabled={!provDisabled}
        deviceSettingsNotFound={deviceSettingsNotFound}
      />
    );
  } else if (fetching) {
    return <LoadingIndicator />;
  } else {
    return (
      <DeviceRouter
        device={device}
        id={deviceId}
        deviceProvEnabled={false}
        accountProvEnabled={false}
        deviceSettingsNotFound={false}
      />
    );
  }
};

const ActionPopups: React.FC<{
  id: string;
  onRestart: () => void;
  onReset: () => void;
}> = ({ id, onRestart, onReset }) => (
  <Switch>
    <ProtectedRoute
      nest
      path={["/delete", "/connected-device/:connectedDeviceId/delete"]}
      requirement="unregister:device"
    >
      <DeleteDeviceModal id={id} />
    </ProtectedRoute>
    <ProtectedRoute nest path="/edit" requirement="update:device">
      <EditDeviceDetailsModal id={id} />
    </ProtectedRoute>
    <ProtectedRoute nest path="/restart" requirement="update:device">
      <RestartDeviceModal id={id} onRestart={onRestart} />
    </ProtectedRoute>
    <ProtectedRoute nest path="/reset" requirement="update:device">
      <FactoryResetDeviceModal id={id} onReset={onReset} />
    </ProtectedRoute>
    <FeatureFlagEnabled feature="CALL_CONTROL">
      <Route nest path="/dialOut">
        <DialOutModal deviceId={id} />
      </Route>
    </FeatureFlagEnabled>
  </Switch>
);

export const DeviceRouter: React.FC<{
  device: DetailProps | undefined;
  id: string;
  deviceProvEnabled: boolean;
  accountProvEnabled: boolean;
  deviceSettingsNotFound?: boolean;
}> = ({
  device,
  id,
  deviceProvEnabled,
  accountProvEnabled,
  deviceSettingsNotFound = false
}) => {
  const { Sidebar } = Layout;
  const { t } = useTranslation();
  const { url } = useRouteMatch();
  const [addNotification] = useNotification();
  const [restarted, setRestarted] = useState(false);
  const [reset, setReset] = useState(false);
  const tenantId = useTenantId();
  const returnURL = generatePath(
    url
      .replace(`/${id}`, "")
      .replace("/insight-device", "")
      .replace("/inventory/details", "/inventory/table")
  );

  const noResultsString = t("DEVICE_DOESNT_EXIST_IN_INVENTORY", { id });

  if (device && device?.tenant?.id !== tenantId) {
    addNotification({
      type: "info",
      id: "deviceWrongAccount",
      message: <p>{t("DEVICE_WRONG_ACCOUNT")}</p>,
      dismiss: DISMISS.DEFAULT
    });

    return <Redirect to="/manage/inventory/table" />;
  }

  const { Content } = Layout;

  const desktopAppModelId = "Lens Desktop";
  const mobileAppModelId = "Lens Mobile";

  const lensDesktop = useIsLensApp(device, desktopAppModelId);
  const lensMobile = useIsLensApp(device, mobileAppModelId);
  const connectedToDesktop = useIsConnectedToApp(device, desktopAppModelId);
  const connectedToMobile = useIsConnectedToApp(device, mobileAppModelId);
  const isCompatibleDesktopVersion = useIsCompatibleVersion(
    device,
    desktopAppModelId
  );
  const isCompatibleMobileVersion = useIsCompatibleVersion(
    device,
    mobileAppModelId
  );
  const tenantRelays = useTenantRelayDevices(tenantId || "", 1);
  const hasRelays = !!tenantRelays.length;

  const showAdvanced = device
    ? compareVersion(device.softwareVersion, MIN_SOFTWARE_FOR_ADVANCED) >= 0
    : false;

  const modelName = device?.hardwareModel ?? "";
  const isRelayDevice = isLensRelay(modelName);
  const isPolyOSDevice = isPolyOS(modelName);
  const isPolyOSProvisioned = isPolyOSDevice && deviceProvEnabled;
  const isEdgeDevice = isEdge(modelName);
  const isEdgeEDevice = isEdgeE(modelName);
  let settingsDisabledReason = "";

  const isDesktopOrMobileDevice = device?.proxyAgent === DESKTOP_PROXY_AGENT;

  const logDownloadMinSWVersion = isPolyOSDevice
    ? MIN_SOFTWARE_FOR_POLYOS_LOG_DOWNLOAD
    : isEdgeDevice
    ? MIN_SOFTWARE_FOR_EDGE_LOG_DOWNLOAD
    : "";

  const isLogDownloadSupported =
    DIAGNOSTICS_SUPPORT.includes(device?.hardwareFamily ?? "") ||
    (device && logDownloadMinSWVersion
      ? compareVersion(
          formatVersion(device.softwareVersion),
          logDownloadMinSWVersion,
          isEdgeDevice
        ) >= 0
      : false);
  const isLogToFlashSupported = device
    ? isPolyOSDevice &&
      compareVersion(
        device.softwareVersion,
        MIN_SOFTWARE_FOR_POLYOS_LOG_FLASH
      ) >= 0
    : false;
  const isPcapDownloadSupported =
    DIAGNOSTICS_SUPPORT.includes(device?.hardwareFamily ?? "") &&
    // In Edge Family, PCAP is supported only for EdgeE (not for EdgeB)
    (!isEdgeDevice || isEdgeEDevice);

  const deviceSupportsSettings =
    !!device?.supportsSettings && !lensDesktop && !lensMobile;
  const deviceSupportsSoftwareUpdate = !!device?.supportsSoftwareUpdate;

  const deviceHasOptions = deviceHasExtraOptions(
    deviceSupportsSettings,
    deviceSupportsSoftwareUpdate,
    deviceProvEnabled
  );

  const isOnline = device?.connected ?? false;
  let enableSettingsLink: boolean | null;
  // Poly OS devices
  if (isPolyOSDevice) {
    if (isPolyOSProvisioned) {
      // Provisioned devices
      enableSettingsLink = accountProvEnabled || !!device?.connected;
      settingsDisabledReason = t("PROVISIONING_SETTINGS_DISABLED");
    } else {
      // Pin on-boarded devices (troubleshooting mode)
      enableSettingsLink = !!device?.connected && !deviceSettingsNotFound;

      if (!device?.connected) {
        settingsDisabledReason = t("DEVICE_SETTINGS_OFFLINE");
      } else if (deviceSettingsNotFound) {
        settingsDisabledReason = t("POLY_OS_SETTINGS_UNAVAILABLE");
      }
    }
  } else if (isDesktopOrMobileDevice) {
    enableSettingsLink =
      !!device?.connected &&
      ((connectedToDesktop && isCompatibleDesktopVersion) ||
        (connectedToMobile && isCompatibleMobileVersion));

    if (connectedToDesktop && !isCompatibleDesktopVersion) {
      settingsDisabledReason = t("LENS_DESKTOP_VERSION_INCOMPATIBLE");
    }
    if (connectedToMobile && !isCompatibleMobileVersion) {
      settingsDisabledReason = t("LENS_MOBILE_VERSION_INCOMPATIBLE", {
        android_version: "2099"
      });
    }
    if (!device?.connected) {
      settingsDisabledReason = t("DEVICE_SETTINGS_OFFLINE");
    }
    if (!connectedToDesktop) {
      settingsDisabledReason = t("DESKTOP_DEVICE_SETTINGS_OFFLINE");
    }
    if (!connectedToMobile) {
      settingsDisabledReason = t("MOBILE_DEVICE_SETTINGS_OFFLINE");
    }
  } else if (isRelayDevice) {
    enableSettingsLink = true;
  }
  // UCS devices (Trio/VVX/CCX)
  else {
    enableSettingsLink = accountProvEnabled;
    settingsDisabledReason = t("PROVISIONING_SETTINGS_DISABLED");
  }

  let showProvisionedSettings =
    !isPolyOSDevice || (deviceProvEnabled && accountProvEnabled);

  if (isDesktopOrMobileDevice) {
    showProvisionedSettings = false;
  }

  return (
    <div className="device-details-content">
      <Layout
        back={{
          to: returnURL,
          persist: ["InsightsFilterState", "DeviceFilterState"],
          iconDesc: t("RETURN_TO_DEVICE_LIST"),
          title: t(
            url.includes("/insights") ? "INSIGHT_DETAILS" : "DEVICE_LIST"
          )
        }}
      >
        {device ? (
          <>
            <Sidebar>
              <div className="text-center image-placeholder">
                <ProductImageInContainer
                  hardwareModel={device.hardwareModel || device.name}
                  className="mt-25"
                  maxHeight={175}
                />
              </div>
              <ul className="menu router">
                <li>
                  <NavLink
                    persist={["DeviceFilterState", "InsightsFilterState"]}
                    nest
                    exact
                    to=""
                  >
                    {t("DETAILS")}
                  </NavLink>
                </li>
                <li>
                  {enableSettingsLink ? (
                    <>
                      {deviceSupportsSettings && (
                        <>
                          <NavLink
                            persist={[
                              "DeviceFilterState",
                              "InsightsFilterState"
                            ]}
                            nest
                            exact={false}
                            to="/settings"
                          >
                            {t("SETTINGS")}
                          </NavLink>
                          <Route nest path="/settings">
                            {isRelayDevice ? (
                              <RelaySettingsNav />
                            ) : showProvisionedSettings ? (
                              <ProvisionedSettingsNav
                                id={id}
                                model={modelName}
                                siteId={device.site?.id}
                              />
                            ) : isDesktopOrMobileDevice ? (
                              <SettingsNavDesktop />
                            ) : (
                              <SettingsNav
                                showAdvanced={showAdvanced}
                                settings={DEVICE_SETTINGS}
                              />
                            )}
                          </Route>
                        </>
                      )}
                      {deviceSupportsSoftwareUpdate &&
                        !showProvisionedSettings &&
                        !isDesktopOrMobileDevice && (
                          <NavLink
                            persist={[
                              "DeviceFilterState",
                              "InsightsFilterState"
                            ]}
                            nest
                            to="/update/sw"
                          >
                            {t("SOFTWARE_UPDATE")}
                          </NavLink>
                        )}
                    </>
                  ) : (
                    deviceSupportsSettings && (
                      <ToolTip
                        iconTitle={t("SETTINGS")}
                        position="bottom"
                        className="settings-help"
                        iconReplacement={
                          <NavLink
                            persist={[
                              "DeviceFilterState",
                              "InsightsFilterState"
                            ]}
                            to="/settings"
                            disabled
                          >
                            {t("SETTINGS")}
                          </NavLink>
                        }
                      >
                        <p className="mb-0">{settingsDisabledReason}</p>
                      </ToolTip>
                    )
                  )}
                </li>
                {(isLogDownloadSupported || isPcapDownloadSupported) &&
                  deviceHasOptions &&
                  !isDesktopOrMobileDevice && (
                    <li>
                      <NavLink
                        persist={["DeviceFilterState", "InsightsFilterState"]}
                        nest
                        exact
                        to="/troubleshooting"
                      >
                        {t("TROUBLESHOOTING")}
                      </NavLink>
                      <Route nest path="/troubleshooting">
                        <DiagnosticsNav
                          isLogDownloadSupported={isLogDownloadSupported}
                          isPolyOSDevice={isPolyOSDevice}
                          isPcapDownloadSupported={isPcapDownloadSupported}
                          hasRelays={hasRelays}
                          tenantId={tenantId}
                        />
                      </Route>
                    </li>
                  )}

                {!lensDesktop && !lensMobile && (
                  <li>
                    <NavLink
                      persist={["DeviceFilterState", "InsightsFilterState"]}
                      nest
                      to="/analysis"
                    >
                      {t("ANALYSIS")}
                    </NavLink>
                  </li>
                )}
              </ul>
            </Sidebar>
            <Content scrolling className="device-details">
              <Switch>
                <Route nest path="/settings">
                  {isRelayDevice ? (
                    <RelaySettingsLoader device={device} />
                  ) : showProvisionedSettings ? (
                    <ProvisionedDeviceSettingsRouter
                      id={id}
                      model={modelName}
                      siteId={device.site?.id}
                      siteName={device.site?.name}
                    />
                  ) : isDesktopOrMobileDevice ? (
                    <DesktopDeviceSettingsRouter device={device} />
                  ) : (
                    <DeviceSettingsRouter
                      device={device}
                      id={id}
                      showAdvanced={showAdvanced}
                    />
                  )}
                </Route>
                <Route nest path="/update">
                  {!showProvisionedSettings && (
                    <DeviceSettingsRouter
                      device={device}
                      id={id}
                      showAdvanced={showAdvanced}
                    />
                  )}
                </Route>
                <Route nest path="/troubleshooting">
                  <DeviceHeader
                    device={device}
                    pageTitle={
                      <>
                        {device?.hardwareModel}
                        <p className="inline-block device-header-name">{` | ${device?.name ||
                          device?.room?.name ||
                          device?.macAddress}`}</p>
                      </>
                    }
                  />
                  <DiagnosticsRouter
                    deviceId={device.id}
                    tenantId={tenantId ?? ""}
                    deviceModel={device.hardwareModel ?? ""}
                    siteId={device.site?.id ?? ""}
                    isPolyOs={isPolyOSDevice}
                    connected={isOnline}
                    isLogDownloadSupported={isLogDownloadSupported}
                    isPcapDownloadSupported={isPcapDownloadSupported}
                    isLogToFlashSupported={isLogToFlashSupported}
                    hasRelays={hasRelays}
                  />
                </Route>
                <Route nest path="/analysis">
                  <DeviceAnalysis device={device} />
                </Route>
                <Route nest path="">
                  <DeviceHeader
                    device={device}
                    pageTitle={
                      <>
                        {device?.hardwareModel
                          ? `${device?.hardwareModel}`
                          : null}
                        <p
                          className={`inline-block ${
                            device?.hardwareModel
                              ? "device-header-name"
                              : "device-header-name-no-model"
                          }`}
                        >
                          {(device?.hardwareModel ||
                            device?.connected !== null) &&
                            " | "}
                          {device?.name ||
                            device?.room?.name ||
                            device?.macAddress}
                        </p>
                      </>
                    }
                  />
                  <DeviceDetails
                    device={device}
                    reset={reset}
                    restarted={restarted}
                  />
                </Route>
              </Switch>
            </Content>
          </>
        ) : (
          <NoResultsIndicator title={noResultsString} />
        )}
      </Layout>
      <ActionPopups
        id={id}
        onRestart={() => setRestarted(true)}
        onReset={() => setReset(true)}
      />
    </div>
  );
};

export default DeviceLoader;
